import { getPreferredPhone, useGetAllContacts } from "@api/contactService";
import MaestroLoader from "@components/MaestroLoader";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiUrl, authenticatedApi, getAccessToken } from "api";
import { memo, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
// import GridLayout from "react-grid-layout";
import GridLayout, { Responsive, WidthProvider } from "react-grid-layout";
import Sheet from '@mui/joy/Sheet';
import DashboardWidget from '@components/widgets/DashboardWidget';
import { Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, Grid, Modal, ModalClose, ModalDialog, Stack, Switch, Tooltip, Typography } from '@mui/joy';
import Dropdown from '@mui/joy/Dropdown';
import IconButton from '@mui/joy/IconButton';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemContent from '@mui/joy/ListItemContent';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import ListDivider from '@mui/joy/ListDivider';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import DashboardWidthProvider from '@providers/DashboardWidthProvider';
import { useDocumentTitle, useRenderCount, useWindowSize } from '@uidotdev/usehooks';
import Subheader from '@components/layout/Subheader';
import { useAuth } from '@providers/AuthProvider';

import Table from '@mui/joy/Table';
import EnhancedTableHead from "@components/EnhancedTableHead";
import Paginate from "@components/Paginate";
import CreateNewButton from "@components/CreateNewButton";
import ToggleViewMode from "@components/ToggleViewMode";
import ToggleMultiSelect from "@components/ToggleMultiSelect";
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import EditButton from "../EditButton";
import { ObjectContext } from "@providers/ObjectProvider";
import pluralize from 'pluralize';
import UnrecoverableError from "@components/UnrecoverableError";
import toast from "react-hot-toast";
import { FormProvider, useForm } from "react-hook-form";
import { Form } from "@components/forms/Form";
import SaveButtons from "@components/SaveButtons";
import DeleteButton from "@components/DeleteButton";
import { detectObjectType } from "@utils/detectObjectType";
import { CheckPermission } from "@components/CheckPermission";
import Uploady, { useBatchAddListener, useBatchFinishListener, useRequestPreSend, useUploady } from "@rpldy/uploady";
import { isValidDate } from "@utils/dates";
import { events } from "@utils/analytics";

// this entire wrapper is so we can access uploady on the create screen
// ideally we could remove this if we can figure out how to detect the # of pending images
const ObjectEdit = memo(function ObjectEdit({
    create = false,
    query,
    mutation,
    deleteMutation,
    type,
    permissionType = null,
    title,
    subtitleKey = 'name',
    headerActions = null,
    color,
    // columns,
    // listCell,
    // listActions = () => {},
    // defaultParams:_defaultParams = { order: 'asc', orderby: 'name', page: 1, view: 'active' },
    children = <></>,
    ...restProps
}) {
    const { id } = useParams();

    let objectType = type;//detectObjectType(data ?? {}) ?? '';
    if(objectType === 'workplan') {
        objectType = 'work-plan';
    }

    const endpoint = pluralize(objectType); // todo: abstract this
    const uploadUrl = `${apiUrl}${endpoint}/${id ?? 0}/files`;

    const shouldAutoUpload = id ?? false;

    return (
        <Uploady
            multiple
            grouped={false} // keep this false (used for determining our pending file upload count)
            maxGroupSize={1} // keep this 1 (used for determining our pending file upload count)
            method="POST"
            destination={{ url: uploadUrl }}
            autoUpload={shouldAutoUpload}
        >
            <ObjectEditInner
                create={create}
                query={query}
                mutation={mutation}
                deleteMutation={deleteMutation}
                type={type}
                permissionType={permissionType}
                title={title}
                subtitleKey={subtitleKey}
                headerActions={headerActions}
                color={color}
                {...restProps}
            >
                {children}
            </ObjectEditInner>
        </Uploady>
    );
});
export default ObjectEdit;

const ObjectEditInner = memo(function ObjectEditInner({
    create = false,
    query,
    mutation,
    deleteMutation,
    type,
    permissionType = null,
    title,
    subtitleKey = 'name',
    headerActions = null,
    color,
    canEdit = null,
    // columns,
    // listCell,
    // listActions = () => {},
    // defaultParams:_defaultParams = { order: 'asc', orderby: 'name', page: 1, view: 'active' },
    children = <></>,
    ...restProps
}) {
    // useDocumentTitle(`${title} | Maestro Community Manager`);
    const navigate = useNavigate();
    const { id } = useParams(); // todo: maybe swap this to a state param for uploading on a new objec
    const [objId, setObjId] = useState(id);

    const { user, can } = useAuth();

    const uploady = useUploady();

    // the token is read per request; reading it once at module scope freezes
    // whatever value existed when the bundle loaded
    useRequestPreSend(({ options }) => ({
        options: {
            destination: {
                ...options.destination,
                headers: {
                    ...options.destination?.headers,
                    Authorization: `Bearer ${getAccessToken()}`,
                },
            },
        },
    }));

    // const renders = useRenderCount();

    const {
        isPending,
        isLoading,
        isError,
        error,
        data,//: loadedData,
        isFetching,
        isPreviousData,
    } = query(parseInt(id), {
        retry: false,
        enabled: !create,
    });

    /*
    const [data, setData] = useState(undefined);
    useEffect(() => {
      setData(loadedData);
    }, [loadedData]);
    */

    /*
    let pageTitle;
    if(createMode) {
        pageTitle = 'New '
    }
    */
    const pageTitle = `${create? 'Create' : 'Edit'} ${data?.name ?? data?.address ?? pluralize.singular(title ?? '')}`;
    useDocumentTitle(`${pageTitle} | Maestro Community Manager`);

    const goToShow = (idToUse = null) => {
        const _id = idToUse || objId;
        // replace create with edit
        navigate(`../${_id}/edit`, { replace: true });

        // then navigate to show
        setTimeout(() => {
            navigate('../.');
        }, 5);
    }

    /*
    |--------------------------------------------------------------------------
    | Remote Change Notifier
    |--------------------------------------------------------------------------
    */
    // notify if the user makes a change in another tab
    /**
     * ISSUES TO WORK THROUGH:
     * 1. This displays when you load a different object.
     */
    const [showDataChangedAlert, setShowDataChangedAlert] = useState(false);
    const [startingData, setStartingData] = useState(null);
    /*
    useEffect(() => {
        if(startingData === null && data !== undefined) {
            console.log(data);
            setStartingData(data);
            return;
        }

        if(
            // todo: need to compare these better
            JSON.stringify(data) != JSON.stringify(startingData)
            // && !isPending
        ) {
            if(data.id === startingData.id) {
                setShowDataChangedAlert(true);
            } else {
                setStartingData(data);
            }
        }

      }, [data]);

    // todo - disable editing while updateObject.isPending === true
    // console.log(data?.id);
    */

    /*
    |--------------------------------------------------------------------------
    | Setup
    |--------------------------------------------------------------------------
    */
    // const objType = pluralize.singular(title.toLowerCase() ?? '');
    let objectType = type;//detectObjectType(data ?? {}) ?? '';
    if(objectType === 'workplan') {
        objectType = 'work-plan';
    }
    let objectSingularName = objectType.replace('-', ' ');

    const mutateId = parseInt(data?.id ?? 0);
    const updateObject = mutation({id: mutateId});
    const deleteObject = deleteMutation({id: mutateId});

    /*
    |--------------------------------------------------------------------------
    | Files Handling
    |--------------------------------------------------------------------------
    */
    const [pendingUploadCount, setPendingUploadCount] = useState(0);
    const endpoint = pluralize(objectType); // todo: abstract this
    // const shouldAutoUpload = !!data?.id;
    const uploadUrl = `${apiUrl}${endpoint}/${data?.id ?? 0}/files`; // this is correct

    useBatchAddListener((batch, options) => {
        // only on create screen; edit screen is immediate
        if(create) {
            setPendingUploadCount(pendingUploadCount+1);
        }
    });

    useBatchFinishListener((batch, options) => {
        events.fileUploaded();

        if(create) {
            let newCount = pendingUploadCount-1;
            setPendingUploadCount(Math.max(0, newCount));

            if(newCount <= 0) {
                // uploads are done
                toast.success('Files uploaded!', {
                    id: 'files-uploading',
                });

                goToShow();
            }
        }
    });

    /*
    |--------------------------------------------------------------------------
    | Delete
    |--------------------------------------------------------------------------
    */
    function onDelete() {
        const performDelete = deleteObject.mutateAsync();

        toast.promise(performDelete, {
            loading: 'Deleting...',
            success: `The ${objectSingularName} was deleted.`,
            error: 'There was an error while deleting.',
        });

        performDelete.then((resp) => {
            events.objectDeleted({ type: type });

            navigate('../../.'); // the extra . is so we don't end up with a / at the end of the url
        });
    }

    /*
    |--------------------------------------------------------------------------
    | Save
    |--------------------------------------------------------------------------
    */
    function onSubmit(saveData, error) {
        let newObj = {...saveData};

        console.log(saveData);

        // nuke anything that is to_be_deleted
        for(const key in newObj) {
            if(
                typeof newObj[key] !== 'object'
                || (newObj[key] instanceof Date) /*isValidDate(newObj[key])*/
                || !newObj[key]
            ) {
                continue;
            }

            let relationship = newObj[key];

            for(let i = relationship.length - 1; i >= 0; i--) {
                if(!!relationship[i].to_be_deleted) {
                    relationship.splice(i, 1);
                }
            }
        }

        if(typeof restProps.beforeSave === 'function') {
            newObj = restProps.beforeSave(newObj);
        }

        console.log(newObj);

        const performUpdate = updateObject.mutateAsync(newObj);

        toast.promise(performUpdate, {
            loading: `${create?'Creating':'Saving'}...`,
            success: `The ${objectSingularName} was ${create?'created':'saved'}!`,
            error: `There was an error while ${create?'creating':'saving'}.`,
        });

        performUpdate.then((newData) => {
            // setStartingData(newData); // reset save modal
            setStartingData(null);

            setObjId(newData.id);

            if(create) {
                events.objectCreated({ type: type });

                if(pendingUploadCount === 0) {
                    goToShow(newData.id);
                } else {
                    /*const toastId =*/ toast.loading(`Uploading file(s)...`, {
                        id: 'files-uploading',
                    });
                    //`Uploading ${} ${pluralize('file', data.total)}`
                    // process file uploads

                    // NOTE: goToShow is called in useBatchFinishListener
                    const createdUploadUrl = `${apiUrl}${endpoint}/${newData.id}/files`;
                    uploady.processPending({
                        destination: {
                            url: createdUploadUrl,
                        }
                    });
                }
            } else {
                events.objectEdited({ type: type });

                navigate('../.');
            }
        });
    }

    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */
    const hasPermissionToEdit = () => {
        if(canEdit !== null) {
            return canEdit({data});
        }

        if(!permissionType) {
            return true;
        }

        return can([`edit basic ${permissionType}`, `edit ${permissionType}`]);
    }
    // <CheckPermission permissions={!permissionType ? true : [`edit ${permissionType}`]}>
    if(!isLoading && permissionType !== null && !hasPermissionToEdit() /*!can([`edit basic ${permissionType}`, `edit ${permissionType}`])*/) {
        return (
            <UnrecoverableError status={403}/>
        );
    }

    if(isLoading || isError) {
        return (<>
            <Subheader
                title={title}
                // subtitle={''}
                tint={color}
            >
                {/* This icon button is used as placeholder to keep the page from changing height */}
                <IconButton
                    size="sm"
                    color="neutral"
                    variant="outlined"
                    sx={{ bgcolor: 'background.surface', opacity: 0, pointerEvents: 'none' }}
                >
                    <ChevronRightRoundedIcon />
                </IconButton>
            </Subheader>
            {isLoading && (
                <MaestroLoader centered/>
            )}
            {isError && (
                <UnrecoverableError status={404} />
            )}
        </>
        );
    }

    return (<>
        <ObjectContext.Provider value={data/*loadedData*/}>
            {/*
            <Uploady
                multiple
                grouped
                maxGroupSize={1}
                method="POST"
                destination={{ url: uploadUrl }}
                autoUpload={shouldAutoUpload}
            >
            */}
            <Form onSubmit={onSubmit} initialData={restProps?.initialData} style={{minHeight: 'calc(100% - var(--Header-height))'}}>
                <Subheader
                    title={title}
                    //title={`${title} renders: ${renders}`}
                    subtitle={data ? data[subtitleKey] : ''}
                    tint={color}
                >
                    <Stack sx={{gap: 1, flexDirection: 'row'}}>
                        {!create && (
                        <CheckPermission permissions={!permissionType ? true : [`edit ${permissionType}`]}>
                            <DeleteButton onDelete={onDelete} sx={{mr: 2}} />
                        </CheckPermission>
                        )}
                        {headerActions}
                        <SaveButtons/>
                    </Stack>
                </Subheader>
                <Modal
                    open={showDataChangedAlert}
                    // onClose={() => setShowDataChangedAlert(false)} // do not enable this - force the user to press a button
                >
                    <ModalDialog
                        color="danger"
                        variant="outlined"
                    >
                        <DialogTitle>
                            <WarningRoundedIcon />
                            {pluralize.singular(title.replace('Edit ','') ?? 'Data')} Modified
                        </DialogTitle>
                        <Divider />
                        <DialogContent>
                            {data?.name ?? data?.address ?? 'This'} was modified while you were away.
                            <br/>
                            Would you like to reload? Your changes will be lost.
                        </DialogContent>
                        <DialogActions>
                            <Button variant="solid" color="danger" onClick={() => {window.location.reload();setShowDataChangedAlert(false)}}>
                            Reload
                            </Button>
                            <Button variant="plain" color="neutral" onClick={() => setShowDataChangedAlert(false)}>
                            Cancel
                            </Button>
                        </DialogActions>
                    </ModalDialog>
                </Modal>

                <Box sx={theme => ({
                    backgroundImage: color && `linear-gradient(180deg, ${theme.palette[color][100]} 0%, white 100%);`,// : null,
                    backgroundAttachment: 'fixed',
                    height: '100%',
                    p: 2,
                })}>
                <Grid container columns={12} gap={2}>
                {/* <div onClick={doSave}>test updating name</div> */}
                    {children}
                </Grid>
                </Box>
            </Form>
            {/* </Uploady> */}
        </ObjectContext.Provider>
    </>);

});